import { DetailCard } from 'xui-pro';
import { List, Tabs, Tag, Spin } from 'antd';
import { useRequest } from 'ahooks';
import { useEffect, useState, useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';
import { useTranslation } from '@/hooks/useTranslation';
import {
  listSecondChannels,
  listArticlePage,
  type OpenArticleItemVo,
  type OpenArticlePage,
  type OpenSecondChannelVo
} from '@/api/sip/notification';
import { data } from '@/models/notificationData';
import { formatNoticeTime } from '@/utils/formatTime';
import styles from './index.module.less';

const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];
const DEFAULT_PAGE_SIZE = 10;

/**
 * 国内（isGlobal=false）—— 用写死数据 `data`，不请求 /web/sip/notice 下的任何接口。
 * 与 develop 分支上的实现保持一致。
 */
const HardcodedList = () => {
  const $ = useTranslation();
  const onClick = useCallback((item: { id: number; title: string }) => {
    window.xDragonBridge.openNewTab({
      path: `/afs-sip/repair-info-search/notice/${item.id}`,
      title: item.title
    });
  }, []);

  return (
    <DetailCard title={$('SysteamNotification')} className={styles['card']}>
      <List
        dataSource={data}
        renderItem={item => (
          <List.Item>
            <List.Item.Meta title={<a onClick={() => onClick(item)}>{item.title}</a>} />
            <div>{item.time}</div>
          </List.Item>
        )}
      />
    </DetailCard>
  );
};

/**
 * 国际（isGlobal=true）—— 走 SIP 接口三件套：
 *   listSecondChannels（拉 Tab）→ 每个 Tab 用 pageSize=1 探测 total 隐藏无文章的 Tab
 *   → listArticlePage 拉当前 Tab 分页数据 → 前端兜底排序（置顶优先 + topTime/publishTime desc）
 */
const ApiList = () => {
  const $ = useTranslation();
  const countryCode = useSelector<RootState, string>(
    state => state.LocaleModel.countryCode,
    shallowEqual
  );

  const [activeChannelId, setActiveChannelId] = useState<number | undefined>();
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data: channels = [], loading: channelLoading } = useRequest<OpenSecondChannelVo[], []>(
    async () => {
      const res = await listSecondChannels();
      if (res.code !== 200) return [];
      return res.data ?? [];
    },
    { manual: false }
  );

  /**
   * 二次过滤：对 listSecondChannels 返回的每个 Tab 用 pageSize=1 请求 listArticlePage
   * 看看 total，只保留 total > 0 的 Tab，无文章的 Tab 隐藏。
   * 并行请求，最小请求体（size=1）省流量。
   */
  const { data: filteredChannels = [], loading: filterLoading } = useRequest<
    OpenSecondChannelVo[],
    []
  >(
    async () => {
      if (!channels.length || !countryCode) return [];
      const totals = await Promise.all(
        channels.map(async ch => {
          try {
            const res = await listArticlePage({
              secondChannelId: ch.channelId,
              countryCode,
              current: 1,
              pageSize: 1
            });
            return res.code === 200 ? res.data?.total ?? 0 : 0;
          } catch {
            return 0;
          }
        })
      );
      return channels.filter((_, i) => totals[i] > 0);
    },
    {
      ready: channels.length > 0 && !!countryCode,
      refreshDeps: [channels, countryCode]
    }
  );

  useEffect(() => {
    if (!filteredChannels.length) return;
    const stillValid = filteredChannels.some(c => c.channelId === activeChannelId);
    if (!stillValid) {
      setActiveChannelId(filteredChannels[0].channelId);
      setCurrent(1);
    }
  }, [filteredChannels, activeChannelId]);

  const handleTabChange = useCallback((key: string) => {
    setActiveChannelId(Number(key));
    setCurrent(1);
  }, []);

  const { data: pageData, loading: listLoading } = useRequest<OpenArticlePage | null, []>(
    async () => {
      if (!activeChannelId || !countryCode) return null;
      const res = await listArticlePage({
        secondChannelId: activeChannelId,
        countryCode,
        current,
        pageSize
      });
      if (res.code !== 200) return null;
      return res.data ?? null;
    },
    {
      ready: activeChannelId !== undefined && !!countryCode,
      refreshDeps: [activeChannelId, countryCode, current, pageSize]
    }
  );

  const articles: OpenArticleItemVo[] = pageData?.records ?? [];
  const total = pageData?.total ?? 0;

  /**
   * 前端兜底排序（当前页内）：
   *   1. 置顶（toped=1）在前
   *   2. 均置顶 → 按 topTime（置顶更新时间）倒序
   *   3. 均非置顶 → 按 publishTime（发布时间）倒序
   *   4. tie-breaker → articleId 倒序（保证稳定）
   */
  const sortedArticles = [...articles].sort((a, b) => {
    const aTop = a.toped === 1 ? 1 : 0;
    const bTop = b.toped === 1 ? 1 : 0;
    if (aTop !== bTop) return bTop - aTop;
    if (aTop === 1) {
      const diff = (b.topTime ?? 0) - (a.topTime ?? 0);
      if (diff !== 0) return diff;
    } else {
      const diff = (b.publishTime ?? 0) - (a.publishTime ?? 0);
      if (diff !== 0) return diff;
    }
    return (b.articleId ?? 0) - (a.articleId ?? 0);
  });

  const onClick = useCallback((item: OpenArticleItemVo) => {
    window.xDragonBridge.openNewTab({
      path: `/afs-sip/repair-info-search/notice/${item.articleUuid}`,
      title: item.title
    });
  }, []);

  return (
    <DetailCard title={$('SysteamNotification')} className={styles['card']}>
      {filteredChannels.length > 0 && (
        <Tabs
          activeKey={activeChannelId !== undefined ? String(activeChannelId) : undefined}
          onChange={handleTabChange}
          className={styles['tabs']}
        >
          {filteredChannels.map(ch => (
            <Tabs.TabPane tab={ch.channelName} key={String(ch.channelId)} />
          ))}
        </Tabs>
      )}
      <Spin spinning={channelLoading || filterLoading || listLoading}>
        <List
          dataSource={sortedArticles}
          renderItem={item => (
            <List.Item>
              <List.Item.Meta
                title={
                  <span className={styles['item-title']}>
                    {item.requiredRead === 1 && (
                      <Tag color='error' className={styles['tag']}>
                        {$('MustRead')}
                      </Tag>
                    )}
                    <a onClick={() => onClick(item)}>{item.title}</a>
                  </span>
                }
              />
              <div>{formatNoticeTime(item.publishTime, countryCode)}</div>
            </List.Item>
          )}
          pagination={{
            current,
            pageSize,
            total,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            showSizeChanger: true,
            showQuickJumper: false,
            showTotal: (t, range) => `${range[0]}-${range[1]} / ${t}`,
            onChange: (page, size) => {
              setCurrent(page);
              if (size !== pageSize) setPageSize(size);
            },
            onShowSizeChange: (_, size) => {
              setCurrent(1);
              setPageSize(size);
            }
          }}
        />
      </Spin>
    </DetailCard>
  );
};

/**
 * 顶层入口：按 `LocaleModel.isGlobal` 分发到对应实现。
 *   - 国内（`isGlobal=false`，`countryCode='CN'`）→ 走写死数据（HardcodedList）
 *   - 国际（`isGlobal=true`，非 CN）→ 走接口（ApiList）
 */
export default () => {
  const isGlobal = useSelector<RootState, boolean>(
    state => state.LocaleModel.isGlobal,
    shallowEqual
  );
  return isGlobal ? <ApiList /> : <HardcodedList />;
};
